import { useCallback, useState } from 'react';
import { SubscriptionRequest } from 'services/subscription/types';
import { createSubscription } from 'services/subscription/subscriptionService';
import { resolveRecurrence } from 'services/payment/rules';
import { REJECTED, resolvePaymentOutcome } from 'services/payment/outcome';
import { REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION } from 'config';
import * as Sentry from '@sentry/react';

interface UseFlywireHandlerOptions {
  language: string;
  objectData: any;
  region: string;
  onError: (type: string) => void;
  setStatus: (status: string) => void;
  setSuccess: (success: boolean) => void;
}

export function useFlywireHandler({ language, objectData, region, onError, setStatus, setSuccess }: UseFlywireHandlerOptions) {
  //const [status, setStatus] = useState<string>();
  //const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleFlywireResponse = useCallback(async (dato: any, sessionToken: string) => {
    if (!dato?.success) {
      onError('payment');
      Sentry.logger.error('subscription error', { session: sessionToken, message: 'Flywire response indicated failure', data: dato });
      return;
    }

    if (resolvePaymentOutcome('flywire', dato?.args?.status) === REJECTED) {
      onError('payment');
      Sentry.logger.error('subscription error', { session: sessionToken, message: 'Flywire response indicated error status', error: dato?.args.error });
      return;
    }

    setResult(dato.args);
    setSuccess(true);
    setStatus(dato.args.status);

    if (['card', 'credit_card', 'recurring'].includes(dato.args.paymentMethod)) {
      let recurrentOption = resolveRecurrence({
        market: region,
        country: objectData.country || '',
        frequency: objectData.frequency,
        automaticApproval: objectData.automaticApproval,
        userAuthorized: Boolean(objectData.recurrent),
        countryException: REACT_APP_RULE_OMIT_PAYMENT_COUNTRY_EXCEPTION,
        isFirstPaymentScreen: true,
      }).recurrent;
      if (dato.args.token) recurrentOption = true;

      const subscriptionData: SubscriptionRequest = {
        reference: dato.args.reference,
        sig: dato.args.sig,
        status: dato.args.status,
        payerAmount: dato.args.payerAmount,
        payerAmountCurrency: dato.args.payerAmountCurrency,
        amount: dato.args.amount === undefined ? dato.args.amount : String(dato.args.amount),
        amountCurrency: dato.args.amountCurrency,
        paymentMethod: dato.args.paymentMethod,
        subscription: {
          id_quotation: objectData.idQuotation,
          id_application: objectData.idApplication,
          recurrent: recurrentOption,
          paymaster: objectData.payingForPolicy,
          paymaster_relationship: objectData.payingForPolicy === 'Other' ? objectData.relationship : objectData.payingForPolicy,
        },
        payer: {
          first_name: objectData.firstName,
          last_name: objectData.lastName,
          middle_name: objectData.middleName,
          address1: objectData.address,
          address2: '',
          city: objectData.cityForFlywire || objectData.city,
          state: objectData.state,
          zip: objectData.zipCode,
          country: objectData.country,
          phone: objectData.phone,
          email: objectData.email,
          paymaster: objectData.payingForPolicy,
          paymaster_relationship: objectData.relationship,
        }
      };

      if (['credit', 'debit'].includes(dato.args.type)) {
        subscriptionData.card = {
          token: dato.args.token,
          type: dato.args.type,
          brand: dato.args.brand,
          digits: dato.args.digits,
          expirationMonth: dato.args.expirationMonth,
          expirationYear: dato.args.expirationYear,
        };
      }

      const res = await createSubscription(subscriptionData, sessionToken, objectData.country, region);
      if (!res.success) {
        console.error('subscription(response)', res.error);
        Sentry.logger.error('subscription error', { session: sessionToken, request: subscriptionData, error: res.error });
      } else {
        console.log('subscription(response)', res.data);
        //Sentry.logger.info('subscription success', { session: sessionToken, request: subscriptionData, response: res.data });
      }
    } else {
      //console.warn('subscription', 'Payment method not supported');
      Sentry.logger.warn('subscription warning', { session: sessionToken, message: 'Payment method not supported', paymentMethod: dato.args.paymentMethod });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectData, onError]);

  return { handleFlywireResponse, result };
}
